import { NgClass, NgTemplateOutlet } from '@angular/common'
import { AfterViewInit, Component, computed, effect, ElementRef, input, OnDestroy, output, signal, ViewChild } from '@angular/core'
import { DropdownComponent } from '@app/v2/shared/components/dropdown/dropdown.component'
import { DropdownOption } from '@app/v2/shared/components/dropdown/types/dropdown-option.type'
import { InvitationTrackerPersona } from '../../../../types/invitation-tracker.type'

const TAB_GAP = 8
const DESKTOP_BREAKPOINT = '(min-width: 1024px)'

@Component({
  selector: 'persona-tabs',
  standalone: true,
  imports: [NgClass, NgTemplateOutlet, DropdownComponent],
  templateUrl: './persona-tabs.component.html',
  host: {
    class: 'tw-block tw-w-full tw-sticky tw-top-0 tw-z-20 lg:tw-static'
  }
})
export class InvitationTrackerPersonaTabsComponent implements AfterViewInit, OnDestroy {
  public readonly personas = input.required<InvitationTrackerPersona[]>()
  public readonly selectedPersonaId = input<number | null>(null)

  public readonly personaSelected = output<number>()

  @ViewChild('tabsContainer') private _tabsContainer?: ElementRef<HTMLElement>
  @ViewChild('measureRow') private _measureRow?: ElementRef<HTMLElement>

  public readonly isMoreOpen = signal(false)
  public readonly layoutMeasured = signal(false)
  public readonly visibleCount = signal<number | null>(null)

  /**
   * Returns the visible personas.
   */
  public readonly visiblePersonas = computed(() => {
    const all = this.personas()
    const count = this.visibleCount()
    if (!this.layoutMeasured() || count === null) return []
    if (count >= all.length) return all

    const selectedId = this.selectedPersonaId()
    const selectedIndex = all.findIndex((persona) => persona.id === selectedId)
    if (selectedIndex === -1 || selectedIndex < count) return all.slice(0, count)

    const visible = all.slice(0, count - 1)
    visible.push(all[selectedIndex])
    return visible
  })

  /**
   * Returns the overflow personas.
   */
  public readonly overflowPersonas = computed(() => {
    const visibleIds = new Set(this.visiblePersonas().map((persona) => persona.id))
    return this.personas().filter((persona) => !visibleIds.has(persona.id))
  })

  /**
   * Returns the overflow dropdown options.
   */
  public readonly overflowDropdownOptions = computed<DropdownOption[]>(() =>
    this.overflowPersonas().map((persona) => ({
      key: persona.id,
      value: persona.name.toUpperCase()
    }))
  )

  public readonly moreDropdownHeight = computed(() => Math.max(this.overflowPersonas().length * 48, 48))

  private _viewReady = false
  private _resizeObserver: ResizeObserver | null = null
  private _desktopMediaQuery: MediaQueryList | null = null
  private _resizeFrameId: number | null = null
  private readonly _onDesktopBreakpointChange = (): void => {
    if (this._desktopMediaQuery?.matches) this.layoutMeasured.set(false)
    this._scheduleRecalculate()
  }

  constructor() {
    effect(() => {
      this.personas()
      this.selectedPersonaId()
      if (!this._viewReady) return
      this._scheduleRecalculate()
    })
  }

  ngAfterViewInit(): void {
    if (typeof window !== 'undefined') {
      this._desktopMediaQuery = window.matchMedia(DESKTOP_BREAKPOINT)
      this._desktopMediaQuery.addEventListener('change', this._onDesktopBreakpointChange)
    }

    this._viewReady = true
    this._recalculateVisibleCount()

    if (typeof ResizeObserver !== 'undefined' && this._tabsContainer) {
      this._resizeObserver = new ResizeObserver(() => this._scheduleRecalculate())
      this._resizeObserver.observe(this._tabsContainer.nativeElement)
    }
  }

  ngOnDestroy(): void {
    this._resizeObserver?.disconnect()
    this._desktopMediaQuery?.removeEventListener('change', this._onDesktopBreakpointChange)
    if (this._resizeFrameId != null) cancelAnimationFrame(this._resizeFrameId)
  }

  /**
   * Selects a persona tab.
   */
  public onPersonaClick(personaId: number): void {
    if (personaId === this.selectedPersonaId()) return
    this.personaSelected.emit(personaId)
  }

  /**
   * Handles overflow persona selection from the dropdown.
   */
  public onOverflowOptionClicked(option: DropdownOption): void {
    const personaId = Number(option.key)
    if (!Number.isFinite(personaId)) return
    this.onPersonaClick(personaId)
  }

  /**
   * Tracks when the More dropdown opens.
   */
  public onMoreDropdownOpened(): void {
    this.isMoreOpen.set(true)
  }

  /**
   * Tracks when the More dropdown closes.
   */
  public onMoreDropdownClosed(): void {
    this.isMoreOpen.set(false)
  }

  /**
   * Schedules a recalculation of the visible count.
   */
  private _scheduleRecalculate(): void {
    if (this._resizeFrameId != null) cancelAnimationFrame(this._resizeFrameId)
    this._resizeFrameId = requestAnimationFrame(() => {
      this._resizeFrameId = null
      this._recalculateVisibleCount()
    })
  }

  /**
   * Recalculates the visible count.
   */
  private _recalculateVisibleCount(): void {
    const all = this.personas()

    if (!this._isDesktopViewport() || all.length === 0) {
      this.visibleCount.set(all.length)
      this.layoutMeasured.set(true)
      return
    }

    if (!this._viewReady) return

    const container = this._tabsContainer?.nativeElement
    const measureRow = this._measureRow?.nativeElement
    if (!container || !measureRow) return

    const tabWidths = (Array.from(measureRow.querySelectorAll('[data-measure-tab]')) as HTMLElement[]).map((tab) => tab.offsetWidth)
    const moreButtonWidth = (measureRow.querySelector('[data-measure-more]') as HTMLElement | null)?.offsetWidth ?? 0
    const containerWidth = container.clientWidth
    const selectedIndex = all.findIndex((persona) => persona.id === this.selectedPersonaId())
    let count = 1

    while (count < all.length && this._rowWidth(tabWidths, count + 1, selectedIndex, moreButtonWidth) <= containerWidth) {
      count++
    }

    this.visibleCount.set(count)
    this.layoutMeasured.set(true)
  }

  /**
   * Returns the rendered row width for a visible count, including the pinned selected tab and the More button.
   */
  private _rowWidth(tabWidths: number[], count: number, selectedIndex: number, moreButtonWidth: number): number {
    if (count >= tabWidths.length) return tabWidths.reduce((total, width) => total + width, 0) + (tabWidths.length - 1) * TAB_GAP

    const isSelectedPinned = selectedIndex >= count
    const leadingWidths = tabWidths.slice(0, isSelectedPinned ? count - 1 : count)
    const visibleWidths = isSelectedPinned ? [...leadingWidths, tabWidths[selectedIndex]] : leadingWidths
    return visibleWidths.reduce((total, width) => total + width, 0) + count * TAB_GAP + moreButtonWidth
  }

  /**
   * Checks if the viewport is desktop.
   * @returns Whether the viewport is desktop.
   */
  private _isDesktopViewport(): boolean {
    return this._desktopMediaQuery?.matches ?? false
  }
}
